import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    PHASE_LABEL,
    PHASE_ORDER,
    TracePhase,
    TraceProgress,
    formatElapsed,
} from '../../api/trace';

export interface ITraceProgressPanelProps {
    /** The steps so far, oldest first. */
    progress: TraceProgress[];
    /** Whether the steps are coming in live, or the fallback is running blind. */
    streaming: boolean;
    /** Abandons the running trace. */
    onCancel: () => void;
}

/**
 * What the trace is doing right now.
 *
 * The panel that replaced a bare spinner, and the reason it exists is that a trace is not
 * one query but up to sixty-two of them over day partitions with tens of thousands of rows
 * in each. Behind a spinner, twenty seconds of that is indistinguishable from a request
 * that has hung - so nobody waited, everybody pressed again, and the second attempt made
 * the first one slower.
 *
 * Every step that will run is listed from the start, greyed out until it does. A list that
 * grows one line at a time cannot say how much is left; a list that is complete from the
 * beginning can, without ever having to guess at a percentage.
 */
export const TraceProgressPanel = (props: ITraceProgressPanelProps) => {
    const byPhase = new Map<TracePhase, TraceProgress>(
        props.progress.map((step) => [step.phase, step])
    );

    const running = props.progress.find((step) => !step.finished);
    const elapsed = props.progress.reduce(
        (most, step) => Math.max(most, step.elapsedMs),
        0
    );

    return (
        <div className="trace-progress" role="status" aria-live="polite">
            <div className="trace-progress__head">
                <FontAwesomeIcon icon="spinner" spin />

                <span className="trace-progress__title">
                    {running ? running.label : 'Bezig met volgen'}
                </span>

                {running?.detail && (
                    <span className="trace-progress__detail">
                        {running.detail}
                    </span>
                )}

                <span className="trace-progress__elapsed">
                    {formatElapsed(elapsed)}
                </span>

                <button
                    type="button"
                    className="requeue-link requeue-link--muted"
                    onClick={props.onCancel}
                >
                    Afbreken
                </button>
            </div>

            {!props.streaming ? (
                <p className="trace-progress__blind">
                    Deze omgeving levert geen live voortgang, dus er valt alleen
                    te wachten. Het zoeken loopt gewoon door.
                </p>
            ) : (
                <ol className="trace-progress__steps">
                    {PHASE_ORDER.map((phase) => {
                        const step = byPhase.get(phase);

                        const state = !step
                            ? 'waiting'
                            : step.finished
                            ? 'done'
                            : 'running';

                        /*
                            A bar only where a real count exists. The log search knows how
                            many days it may read, the timeline knows how many tables it
                            walks; the rest genuinely has no measure, and inventing one is
                            how a progress bar starts lying.
                        */
                        const share =
                            step && step.total > 0
                                ? Math.min(
                                      100,
                                      Math.round((step.done / step.total) * 100)
                                  )
                                : null;

                        return (
                            <li
                                key={phase}
                                className={`trace-progress__step trace-progress__step--${state}`}
                            >
                                <span className="trace-progress__icon">
                                    {state === 'done' ? (
                                        <FontAwesomeIcon icon="check" />
                                    ) : state === 'running' ? (
                                        <FontAwesomeIcon icon="spinner" spin />
                                    ) : (
                                        <FontAwesomeIcon icon="circle" />
                                    )}
                                </span>

                                <span className="trace-progress__label">
                                    {PHASE_LABEL[phase]}
                                </span>

                                <span className="trace-progress__note">
                                    {step?.finished
                                        ? step.result
                                        : step?.detail ?? ''}
                                </span>

                                {share !== null && state !== 'waiting' && (
                                    <span className="trace-progress__bar">
                                        <span
                                            className="trace-progress__fill"
                                            style={{ width: `${share}%` }}
                                        />
                                    </span>
                                )}

                                {step && (
                                    <span className="trace-progress__time">
                                        {formatElapsed(step.elapsedMs)}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
};
